import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/components/command";
import { api } from "@/lib/api";
import { useSearchMenu } from "./search-provider";

export function CommandMenu() {
  const navigate = useNavigate();
  const { open, setOpen } = useSearchMenu();
  const projects = useQuery({ queryKey: ["projects", 0], queryFn: () => api.projects(0), staleTime: 30_000, enabled: open });

  const targets = useQuery({ queryKey: ["targets"], queryFn: api.targets, staleTime: 30_000, enabled: open });

  const run = (go: () => unknown) => {
    setOpen(false);
    go();
  };

  return (
    <CommandDialog modal open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search or jump to…" />
      <CommandList className="max-h-80">
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Pages">
          <CommandItem value="Overview" onSelect={() => run(() => navigate({ to: "/" }))}>
            Overview
          </CommandItem>
          <CommandItem value="Logs" onSelect={() => run(() => navigate({ to: "/logs" }))}>
            Logs
          </CommandItem>
          <CommandItem value="Intercept handlers" onSelect={() => run(() => navigate({ to: "/intercept" }))}>
            Intercept <ChevronRight /> Handlers
          </CommandItem>
          <CommandItem value="Settings" onSelect={() => run(() => navigate({ to: "/settings" }))}>
            Settings
          </CommandItem>
        </CommandGroup>
        {targets.data?.length ? (
          <CommandGroup heading="Handlers">
            {targets.data.map((t) => (
              <CommandItem
                key={t.name}
                value={`Intercept ${t.name}`}
                onSelect={() => run(() => navigate({ to: "/intercept/$name", params: { name: t.name } }))}
              >
                Intercept <ChevronRight /> {t.name.replace("claude_code.", "")}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
        <CommandGroup heading="Logs">
          {(
            [
              ["logs", "Events"],
              ["metrics", "Metrics"],
              ["traces", "Traces"],
            ] as const
          ).map(([signal, label]) => (
            <CommandItem
              key={signal}
              value={`Logs ${label}`}
              onSelect={() => run(() => navigate({ to: "/logs", search: { signal: signal === "logs" ? undefined : signal } }))}
            >
              Logs <ChevronRight /> {label}
            </CommandItem>
          ))}
        </CommandGroup>
        {projects.data?.length ? (
          <CommandGroup heading="Projects">
            {projects.data.map((p) => (
              <CommandItem
                key={p.project}
                value={`Project ${p.project}`}
                onSelect={() => run(() => navigate({ to: "/", search: { project: p.project } }))}
              >
                Project <ChevronRight /> {p.project}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
